import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';

export interface SeoMetaConfig {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private titleService = inject(Title);
  private meta = inject(Meta);
  private platformId = inject(PLATFORM_ID);

  constructor(@Inject(DOCUMENT) private document: Document) {}

  setMetaTags(config: SeoMetaConfig): void {
    const url = `${environment.siteUrl}${config.path}`;
    const image = config.image ?? `${environment.siteUrl}/assets/og-default.png`;

    this.titleService.setTitle(config.title);

    this.meta.updateTag({ name: 'description', content: config.description });
    this.meta.updateTag({
      name: 'robots',
      content: config.noindex ? 'noindex, nofollow' : 'index, follow',
    });

    // Open Graph
    this.meta.updateTag({ property: 'og:title', content: config.title });
    this.meta.updateTag({ property: 'og:description', content: config.description });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:type', content: config.type ?? 'website' });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:secure_url', content: image });
    this.meta.updateTag({ property: 'og:site_name', content: 'Chambia' });
    this.meta.updateTag({ property: 'og:locale', content: 'es_LA' });

    // Twitter
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: config.title });
    this.meta.updateTag({ name: 'twitter:description', content: config.description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    this.setCanonical(url);
  }

  setCanonical(url: string): void {
    let link: HTMLLinkElement | null = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  setJsonLd(id: string, data: Record<string, unknown>): void {
    // Avoid duplicate script tags on client-side navigation.
    const existing = this.document.getElementById(id);
    if (existing) {
      existing.remove();
    }
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify(data);
    this.document.head.appendChild(script);
  }

  removeJsonLd(id: string): void {
    const existing = this.document.getElementById(id);
    if (existing) existing.remove();
  }

  organizationSchema(): Record<string, unknown> {
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Chambia',
      url: environment.siteUrl,
      logo: `${environment.siteUrl}/assets/logo.png`,
      sameAs: [],
      description:
        'Chambia es un marketplace de empleo WhatsApp-first para LATAM que conecta candidatos y empresas con matching por IA.',
    };
  }

  jobPostingSchema(params: {
    title: string;
    description: string;
    datePosted: string;
    validThrough?: string;
    hiringOrgName: string;
    hiringOrgLogo?: string;
    city: string;
    country: string;
    salaryMin?: number;
    salaryMax?: number;
    currency?: string;
    employmentType?: string;
  }): Record<string, unknown> {
    const schema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: params.title,
      description: params.description,
      datePosted: params.datePosted,
      hiringOrganization: {
        '@type': 'Organization',
        name: params.hiringOrgName,
        logo: params.hiringOrgLogo,
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: params.city,
          addressCountry: params.country,
        },
      },
    };

    if (params.validThrough) {
      schema['validThrough'] = params.validThrough;
    }

    if (params.employmentType) {
      schema['employmentType'] = params.employmentType;
    }

    if (params.salaryMin || params.salaryMax) {
      schema['baseSalary'] = {
        '@type': 'MonetaryAmount',
        currency: params.currency ?? 'MXN',
        value: {
          '@type': 'QuantitativeValue',
          minValue: params.salaryMin,
          maxValue: params.salaryMax,
          unitText: 'MONTH',
        },
      };
    }

    return schema;
  }

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}
